import { TableTypeEnum } from "@selector/shared/openapi";
import { Item, MenuOptionItem, Select } from "widgets";

import { useGenomeViewerStore } from "../../store/store.ts";
import { TableRestriction } from "./settings.ts";
import type { TableTypeItem } from "./TableTypeSelect.tsx";

interface TableRestrictionSelectProps {
  tableType: TableTypeItem;
  tableRestriction: TableRestriction;
  visibleTrackGroups: { organismId: number; contigId: number; groupId: string }[];
  onChange: (tableRestriction: TableRestriction) => void;
}

const tableRestrictionsLabelMap: Record<TableRestriction, string> = {
  [TableRestriction.SELECTION]: "Selected Items",
  [TableRestriction.REGION]: "Current Region",
  [TableRestriction.CONTIG]: "Current Contigs",
  [TableRestriction.GENOME]: "Entire Genomes",
};

const getTableRestrictionsForTableType = (tableType: TableTypeEnum) => {
  return tableType === TableTypeEnum.ORGANISM
    ? [TableRestriction.GENOME]
    : [TableRestriction.SELECTION, TableRestriction.REGION, TableRestriction.CONTIG, TableRestriction.GENOME];
};

export const TableRestrictionSelect = ({
  tableType,
  tableRestriction,
  visibleTrackGroups,
  onChange,
}: TableRestrictionSelectProps) => {
  const tableRestrictions = getTableRestrictionsForTableType(tableType.type);
  const baseNumber = useGenomeViewerStore((state) => ((state.canvas?.width ?? 0) * state.basePerPixel) / 1000);

  const getTableRestrictionBadge = (tableRestriction: TableRestriction) => {
    switch (tableRestriction) {
      case TableRestriction.SELECTION:
        return undefined;
      case TableRestriction.REGION:
        return `${baseNumber.toFixed(baseNumber < 100 ? 2 : 0)} Kb`;
      case TableRestriction.CONTIG:
        return new Set(visibleTrackGroups.map((group) => group.contigId)).size;
      case TableRestriction.GENOME:
        return new Set(visibleTrackGroups.map((group) => group.organismId)).size;
    }
  };

  return (
    <Select
      selectedKey={tableRestriction}
      onSelectionChange={(key) => {
        onChange(key as TableRestriction);
      }}
      width={180}
    >
      {tableRestrictions.map((restriction) => {
        const badge = getTableRestrictionBadge(restriction);
        const label = tableRestrictionsLabelMap[restriction] + (badge != undefined ? ` (${badge})` : "");
        return (
          <Item key={restriction} textValue={label}>
            <MenuOptionItem title={label} />
          </Item>
        );
      })}
    </Select>
  );
};
